import React, { memo, useCallback, RefObject, useImperativeHandle } from 'react';
import { Diagram } from 'dhx-diagram-package';
import { useDidMount } from '@core/utils/hooks/react';
import { IDataItem } from 'dhx-diagram-package/codebase/types/ts-data';
import { LibraryItemType } from '@business/entities/library';
import { FlowChartBlockVariation } from '@modules/course/CourseBuilder/Blocks/FlowChartBlock';
import { OrgChartBlockVariation } from '@modules/course/CourseBuilder/Blocks/OrgChartBlock';

export interface DiagramViewerHandle {
  onExport(type: 'png' | 'pdf', name: string): void;
  onScaleChange(value: number): void;
  onData(data: IDataItem[]): void;
}
export interface DiagramViewerProps {
  id: string;
  data: IDataItem[];
  type: LibraryItemType | FlowChartBlockVariation | OrgChartBlockVariation;
  handleRef?: RefObject<DiagramViewerHandle>;
  noHeight?: boolean;
  style?: React.CSSProperties;
}

export const DiagramViewer = memo((props: DiagramViewerProps) => {
  const { type, noHeight, style } = props;
  const viewer = React.useRef<Diagram | null>(null);

  const onExport = useCallback(
    (type: 'png' | 'pdf', name: string) => {
      if (type === 'png') {
        viewer.current?.export.png({});
      } else {
        viewer.current?.export.pdf({ name: `${name}.pdf` });
      }
    },
    [viewer],
  );

  const onScaleChange = useCallback(
    (value: number) => {
      if (viewer.current) {
        viewer.current.config.scale = value / 100;
        viewer.current.paint();
      }
    },
    [viewer],
  );

  const onData = useCallback(
    (data: IDataItem[]) => {
      if (viewer.current) {
        viewer.current.data.parse(data);
      }
    },
    [viewer],
  );

  useImperativeHandle(props.handleRef, () => ({ onExport, onScaleChange, onData }));

  useDidMount(() => {
    viewer.current = new Diagram(props.id, {
      type: type === LibraryItemType.flowChart ? 'default' : 'org',
      margin: { x: 50 },
      exportStyles: false,
    });
    viewer.current.data.parse(props.data);
  });

  return <div style={noHeight ? props.style : { height: 'calc(100vh - 60px)' }} className="noselect" id={props.id}></div>;
});
